/**
 * buildCharacterSheet — pure function that computes a fully derived CharacterSheet
 * from a CharacterDraft. This is the single source of truth for all character
 * calculations, replacing the ~525-line useCharacterBenefits hook.
 */
import {
  getRace,
  getClass,
  getBackground,
  getFightingStyle,
  getFightingStyleLevel,
  getLandTerrain,
  getCircleSpellsForLevel,
  getPactBoon,
  getFavoredEnemy,
  getNaturalTerrain,
  getMetamagicOption,
  getEldritchInvocation,
  getFeat,
  getBeast,
  getSpell,
} from '@character-creator/data';
import {
  ABILITY_NAMES,
  type AbilityScores,
  type AbilityName,
  type SkillName,
  type ClassFeature,
  type CharacterDraft,
  type CharacterSheet,
  type SpellcastingInfo,
  type RacialSpellcastingInfo,
  type RacialSpellInfo,
  type FamiliarInfo,
  type FeatInfo,
  type LandCircleTerrainInfo,
  type PactBoonInfo,
  type NamedItem,
} from '@character-creator/types';
import { calculateModifier } from '../calculations/ability-scores';
import { calculateRacialBonuses, applyRacialBonuses } from '../calculations/racial';
import { calculateProficiencyBonus } from '../calculations/proficiency';
import { calculateMaxHitPoints } from '../calculations/combat';
import { calculateCommonerHitPoints, COMMONER_HIT_DIE } from '../calculations/commoner';
import { calculateSpellSaveDC, calculateSpellAttackBonus } from '../calculations/spellcasting';

/**
 * Build a fully computed CharacterSheet from a CharacterDraft.
 *
 * This is a pure function with no React dependencies. All game rules
 * for deriving stats, proficiencies, features, and spellcasting are
 * contained here (or delegated to core calculation functions).
 */
export function buildCharacterSheet(draft: CharacterDraft): CharacterSheet {
  const race = draft.raceId ? getRace(draft.raceId) : null;
  const subrace = race?.subraces?.find((s) => s.id === draft.subraceId);
  const background = draft.backgroundId ? getBackground(draft.backgroundId) : null;
  const isCommoner = draft.classLevels.length === 0 && draft.confirmedCommoner;
  const isMulticlass = draft.classLevels.length > 1;

  // Compute characterLevel from classLevels (source of truth) to avoid persist desync bug
  const characterLevel = draft.classLevels.length > 0
    ? draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
    : draft.characterLevel;

  // Get class data for all classes
  const classLevels = draft.classLevels.map((cl) => {
    const classData = getClass(cl.classId);
    return {
      classId: cl.classId,
      className: classData?.name ?? cl.classId,
      level: cl.level,
    };
  });

  // Primary class is the first one chosen
  const primaryClassLevel = draft.classLevels[0];
  const primaryClass = primaryClassLevel ? getClass(primaryClassLevel.classId) : null;

  // --- Ability Scores ---
  const baseScores = draft.abilityScores;
  const racialBonuses = calculateRacialBonuses(race, subrace);
  const afterRacialScores = applyRacialBonuses(baseScores, racialBonuses);

  // Calculate ASI ability score increases and collect feats
  const asiAbilityIncreases: Partial<AbilityScores> = {};
  const feats: FeatInfo[] = [];

  for (const cl of draft.classLevels) {
    if (cl.asiChoices) {
      for (const [, choice] of Object.entries(cl.asiChoices)) {
        if (choice.type === 'ability-increase') {
          for (const inc of choice.increases) {
            const ability = inc.ability as AbilityName;
            asiAbilityIncreases[ability] = (asiAbilityIncreases[ability] || 0) + inc.amount;
          }
        } else if (choice.type === 'feat') {
          const feat = getFeat(choice.featId);
          if (feat) {
            feats.push({
              id: feat.id,
              name: feat.name,
              description: feat.description,
              benefits: feat.benefits,
            });
          }
        }
      }
    }
  }

  // Apply ASI increases to get final scores (capped at 20)
  const finalScores: AbilityScores = { ...afterRacialScores };
  for (const ability of ABILITY_NAMES) {
    if (asiAbilityIncreases[ability]) {
      finalScores[ability] = Math.min(20, finalScores[ability] + (asiAbilityIncreases[ability] || 0));
    }
  }

  // Calculate modifiers
  const modifiers = ABILITY_NAMES.reduce(
    (acc, ability) => {
      acc[ability] = calculateModifier(finalScores[ability]);
      return acc;
    },
    {} as Record<AbilityName, number>,
  );

  // --- Combat Stats ---
  const proficiencyBonus = calculateProficiencyBonus(characterLevel);

  // Hit dice breakdown
  const hitDice: { die: string; count: number }[] = [];
  for (const cl of draft.classLevels) {
    const classData = getClass(cl.classId);
    if (classData) {
      hitDice.push({ die: classData.hitDie, count: cl.level });
    }
  }

  // Primary hit die for display
  const hitDie = isCommoner ? COMMONER_HIT_DIE : primaryClass?.hitDie || COMMONER_HIT_DIE;

  // HP: use core's calculateMaxHitPoints for classed characters, commoner helper for commoners
  const hitPoints = draft.classLevels.length > 0
    ? calculateMaxHitPoints(draft.classLevels, finalScores.constitution)
    : calculateCommonerHitPoints(modifiers.constitution);

  const speed = race?.speed || 30;

  // --- Proficiencies ---

  // Saving throws only from first class
  const savingThrows = primaryClass?.savingThrows || [];

  // Skills - aggregate from all classes
  const allClassSkills: SkillName[] = draft.classLevels.flatMap(
    (cl) => cl.skillProficiencyChoices || [],
  );
  const backgroundSkills = background?.skillProficiencies || [];
  const skills = [...new Set([...allClassSkills, ...backgroundSkills])];

  // Expertise - aggregate from all classes (Rogues and Bards)
  const expertiseSkills: SkillName[] = draft.classLevels.flatMap(
    (cl) => cl.expertiseSkills || [],
  );

  // Armor - from first class + multiclass proficiencies from additional classes
  const armorSet = new Set<string>();
  if (primaryClass) {
    primaryClass.armorProficiencies.forEach((a) => armorSet.add(a));
  }
  for (let i = 1; i < draft.classLevels.length; i++) {
    const classData = getClass(draft.classLevels[i].classId);
    if (classData?.multiclassProficiencies?.armor) {
      classData.multiclassProficiencies.armor.forEach((a) => armorSet.add(a));
    }
  }
  const armor = Array.from(armorSet);

  // Weapons - from first class + multiclass proficiencies
  const weaponSet = new Set<string>();
  if (primaryClass) {
    for (const w of primaryClass.weaponProficiencies) {
      const formatted =
        typeof w === 'string' && (w === 'simple' || w === 'martial')
          ? `${w.charAt(0).toUpperCase() + w.slice(1)} weapons`
          : String(w);
      weaponSet.add(formatted);
    }
  }
  for (let i = 1; i < draft.classLevels.length; i++) {
    const classData = getClass(draft.classLevels[i].classId);
    if (classData?.multiclassProficiencies?.weapons) {
      for (const w of classData.multiclassProficiencies.weapons) {
        const formatted =
          typeof w === 'string' && (w === 'simple' || w === 'martial')
            ? `${w.charAt(0).toUpperCase() + w.slice(1)} weapons`
            : String(w);
        weaponSet.add(formatted);
      }
    }
  }
  const weapons = Array.from(weaponSet);

  // Tools (only from first class and background)
  const toolSet = new Set<string>();
  if (primaryClass) {
    primaryClass.toolProficiencies.forEach((t) => toolSet.add(t));
  }
  const backgroundTools = background?.toolProficiencies || [];
  backgroundTools.forEach((t) => toolSet.add(t));
  const tools = Array.from(toolSet);

  const raceLanguages = race?.languages || [];
  const chosenLanguages = draft.languageChoices || [];
  const languages = [...new Set([...raceLanguages, ...chosenLanguages])];

  // --- Traits & Features ---
  const raceTraits = race?.traits || [];
  const subraceTraits = subrace?.traits || [];
  const racialTraits = [...raceTraits, ...subraceTraits];

  // --- Racial Spellcasting ---
  const racialSpellcasting: RacialSpellcastingInfo[] = [];
  for (const trait of racialTraits) {
    if (!trait.spellGrants) continue;
    const { cantrips: cantripIds, spells: spellGrants, spellcastingAbility } = trait.spellGrants;
    const abilityScore = finalScores[spellcastingAbility];

    const resolvedSpells: RacialSpellInfo[] = [];
    for (const grant of spellGrants) {
      if (characterLevel < grant.level) continue;
      const spell = getSpell(grant.spellId);
      if (!spell) continue;
      resolvedSpells.push({
        spellId: grant.spellId,
        spellName: spell.name,
        spellLevel: spell.level,
        usesPerRest: grant.usesPerRest,
        restType: grant.restType,
      });
    }

    racialSpellcasting.push({
      ability: spellcastingAbility,
      attackBonus: calculateSpellAttackBonus(abilityScore, proficiencyBonus),
      saveDC: calculateSpellSaveDC(abilityScore, proficiencyBonus),
      cantrips: cantripIds,
      spells: resolvedSpells,
      sourceTrait: trait.name,
    });
  }

  // Class features - get features up to the level in each class
  const classFeatures: ClassFeature[] = [];
  for (const cl of draft.classLevels) {
    const classData = getClass(cl.classId);
    if (classData) {
      const features = classData.features.filter((f) => f.level <= cl.level);
      classFeatures.push(...features);
    }

    // Add selected fighting style as a feature (if selected)
    const fightingStyleLevelReq = getFightingStyleLevel(cl.classId);
    if (fightingStyleLevelReq !== null && cl.level >= fightingStyleLevelReq && cl.fightingStyleId) {
      const fightingStyle = getFightingStyle(cl.fightingStyleId);
      if (fightingStyle) {
        classFeatures.push({
          id: `fighting-style-${cl.classId}-${fightingStyle.id}`,
          name: `Fighting Style: ${fightingStyle.name}`,
          level: fightingStyleLevelReq,
          description: fightingStyle.description,
        });
      }
    }
  }

  // --- Spellcasting ---
  // Uses class-table lookup (NOT core's calculateSpellSlots multiclass formula)
  // to produce correct results for single-class half/third casters.
  let spellcasting: SpellcastingInfo | null = null;
  const primarySpellcastingClass = draft.classLevels.find((cl) => {
    const classData = getClass(cl.classId);
    return classData?.spellcasting;
  });

  if (primarySpellcastingClass) {
    const classData = getClass(primarySpellcastingClass.classId);
    if (classData?.spellcasting) {
      const spellAbility = classData.spellcasting.ability;
      const levelIndex = Math.min(primarySpellcastingClass.level, 20) - 1;
      const cantripsKnown = classData.spellcasting.cantripsKnown?.[levelIndex] || 0;

      // Build spell slots from class data
      const spellSlots: Record<string, number> = {};
      const slotsArray = classData.spellcasting.spellSlots?.[levelIndex];
      if (slotsArray) {
        for (let i = 0; i < slotsArray.length; i++) {
          if (slotsArray[i] > 0) {
            spellSlots[String(i + 1)] = slotsArray[i];
          }
        }
      }

      spellcasting = {
        ability: spellAbility,
        attackBonus: calculateSpellAttackBonus(finalScores[spellAbility], proficiencyBonus),
        saveDC: calculateSpellSaveDC(finalScores[spellAbility], proficiencyBonus),
        cantripsKnown,
        spellSlots,
      };
    }
  }

  // --- Subclass Features ---

  // Circle of the Land terrain (Druid subclass feature)
  let landCircleTerrain: LandCircleTerrainInfo | null = null;
  const druidLevel = draft.classLevels.find((cl) => cl.classId === 'druid');
  if (druidLevel?.subclassId === 'circle-of-the-land' && druidLevel.landCircleTerrainId) {
    const terrain = getLandTerrain(druidLevel.landCircleTerrainId);
    if (terrain) {
      landCircleTerrain = {
        id: terrain.id,
        name: terrain.name,
        circleSpells: getCircleSpellsForLevel(terrain.id, druidLevel.level),
      };
    }
  }

  // Pact Boon (Warlock class feature at level 3)
  let pactBoon: PactBoonInfo | null = null;
  const warlockLevel = draft.classLevels.find((cl) => cl.classId === 'warlock');
  if (warlockLevel && warlockLevel.level >= 3 && warlockLevel.pactBoonId) {
    const boon = getPactBoon(warlockLevel.pactBoonId);
    if (boon) {
      pactBoon = {
        id: boon.id,
        name: boon.name,
        description: boon.description,
      };
    }
  }

  // Favored Enemies (Ranger class feature)
  const favoredEnemies: NamedItem[] = [];
  const rangerLevel = draft.classLevels.find((cl) => cl.classId === 'ranger');
  if (rangerLevel && rangerLevel.favoredEnemies) {
    for (const enemyId of rangerLevel.favoredEnemies) {
      const enemy = getFavoredEnemy(enemyId);
      if (enemy) {
        favoredEnemies.push({ id: enemy.id, name: enemy.name });
      }
    }
  }

  // Natural Explorer Terrains (Ranger class feature)
  const naturalExplorerTerrains: NamedItem[] = [];
  if (rangerLevel && rangerLevel.naturalExplorerTerrains) {
    for (const terrainId of rangerLevel.naturalExplorerTerrains) {
      const terrain = getNaturalTerrain(terrainId);
      if (terrain) {
        naturalExplorerTerrains.push({ id: terrain.id, name: terrain.name });
      }
    }
  }

  // Metamagic (Sorcerer class feature)
  const metamagicChoices: NamedItem[] = [];
  const sorcererLevel = draft.classLevels.find((cl) => cl.classId === 'sorcerer');
  if (sorcererLevel && sorcererLevel.metamagicChoices) {
    for (const metamagicId of sorcererLevel.metamagicChoices) {
      const metamagic = getMetamagicOption(metamagicId);
      if (metamagic) {
        metamagicChoices.push({ id: metamagic.id, name: metamagic.name });
      }
    }
  }

  // Eldritch Invocations (Warlock class feature)
  const eldritchInvocations: NamedItem[] = [];
  if (warlockLevel && warlockLevel.eldritchInvocations) {
    for (const invocationId of warlockLevel.eldritchInvocations) {
      const invocation = getEldritchInvocation(invocationId);
      if (invocation) {
        eldritchInvocations.push({ id: invocation.id, name: invocation.name });
      }
    }
  }

  // --- Familiar ---
  let familiar: FamiliarInfo | null = null;
  const hasFindFamiliar = draft.knownSpells.includes('find-familiar');
  if (hasFindFamiliar && draft.selectedFamiliarId) {
    const beast = getBeast(draft.selectedFamiliarId);
    if (beast) {
      familiar = {
        id: beast.id,
        name: beast.name,
        armorClass: beast.armorClass,
        hitPoints: beast.hitPoints.average,
        speed: beast.speed,
        type: beast.type,
      };
    }
  }

  return {
    characterLevel,
    classLevels,
    isMulticlass,
    raceName: race?.name ?? null,
    subraceName: subrace?.name ?? null,
    hitDie,
    hitDice,
    hitPoints,
    speed,
    proficiencyBonus,
    baseScores,
    racialBonuses,
    finalScores,
    modifiers,
    savingThrows,
    skills,
    expertiseSkills,
    armor,
    weapons,
    tools,
    languages,
    racialTraits,
    classFeatures,
    spellcasting,
    racialSpellcasting,
    landCircleTerrain,
    pactBoon,
    favoredEnemies,
    naturalExplorerTerrains,
    metamagicChoices,
    eldritchInvocations,
    asiAbilityIncreases,
    feats,
    familiar,
    isCommoner,
  };
}
